import { useCallback, useEffect, useRef, useState } from "react";
import { DBRequest } from "../../api/dbRequest";

const ACTIVE_STATUSES = new Set([
  "initiated",
  "uploading",
  "uploaded",
  "processing",
]);

export function demUploadStorageKey(projectId) {
  return `dem-upload:${projectId}`;
}

export function readTrackedDemUpload(projectId) {
  try {
    const value = localStorage.getItem(demUploadStorageKey(projectId));
    return value ? JSON.parse(value) : null;
  } catch (error) {
    localStorage.removeItem(demUploadStorageKey(projectId));
    return null;
  }
}

export function isDemUploadProcessing(upload) {
  return ACTIVE_STATUSES.has(upload?.status);
}

export function demUploadStatusLabel(upload, hasActiveDemFile) {
  if (upload?.status === "uploaded") return "DEM is waiting for processing";
  if (isDemUploadProcessing(upload)) return "DEM is being processed";
  return hasActiveDemFile ? "active" : "no active DEM file";
}

export default function useDemUploadStatus({
  projectId,
  onProjectUpdate,
  pollIntervalMs = 3000,
}) {
  const [upload, setUpload] = useState(() =>
    readTrackedDemUpload(projectId)
  );
  const [error, setError] = useState(null);
  const onProjectUpdateRef = useRef(onProjectUpdate);
  onProjectUpdateRef.current = onProjectUpdate;

  useEffect(() => {
    setUpload(readTrackedDemUpload(projectId));
    setError(null);
  }, [projectId]);

  const trackUpload = useCallback(
    (nextUpload) => {
      localStorage.setItem(
        demUploadStorageKey(projectId),
        JSON.stringify(nextUpload)
      );
      setError(null);
      setUpload(nextUpload);
    },
    [projectId]
  );

  const uploadId = upload?.id;
  useEffect(() => {
    if (!uploadId) return undefined;

    let cancelled = false;
    let timeoutId;
    const scheduleNextPoll = () => {
      timeoutId = setTimeout(poll, pollIntervalMs);
    };

    const refreshProject = () => {
      DBRequest({
        path: `projects/${projectId}`,
        onResponse: (project) => {
          if (cancelled) return;
          localStorage.removeItem(demUploadStorageKey(projectId));
          setUpload(null);
          setError(null);
          onProjectUpdateRef.current(project);
        },
        onError: () => {
          if (!cancelled) scheduleNextPoll();
        },
      });
    };

    function poll() {
      DBRequest({
        path: `projects/${projectId}/dem-uploads/${uploadId}`,
        onResponse: (nextUpload) => {
          if (cancelled) return;
          setUpload(nextUpload);

          if (nextUpload.status === "ready") {
            refreshProject();
            return;
          }
          if (nextUpload.status === "failed") {
            localStorage.removeItem(demUploadStorageKey(projectId));
            setError(
              nextUpload.error_message || "DEM processing failed."
            );
            return;
          }
          if (isDemUploadProcessing(nextUpload)) {
            localStorage.setItem(
              demUploadStorageKey(projectId),
              JSON.stringify(nextUpload)
            );
            scheduleNextPoll();
            return;
          }

          localStorage.removeItem(demUploadStorageKey(projectId));
          setError(`DEM upload ended with status: ${nextUpload.status}.`);
        },
        onError: () => {
          if (!cancelled) scheduleNextPoll();
        },
      });
    }

    poll();
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [pollIntervalMs, projectId, uploadId]);

  return {
    upload,
    error,
    isProcessing: isDemUploadProcessing(upload),
    trackUpload,
  };
}
